/**
 * Steuerlogik. Sätze, Regeln, Pflichtangaben und Rechtstexte sind DATEN; die Funktionen
 * werten sie nur aus. Rechtstexte stehen nicht im Ergebnis, sondern als noticeKey,
 * übersetzt über eine eigene Tabelle (TAX_NOTICES).
 *
 * Ergebnisse sind Hinweise, nie Sperren (ST-08). Unbekannte Länder führen immer in die
 * manuelle Prüfung, es gibt keinen stillen Rückfall (WS-01).
 */
import { isEuCountry, normalizeCountry, zoneOfCountry } from "./countries.js";
import { clampRatePercent } from "./money.js";
import type { Cents, CountryCode, Currency, Finding, Language, TaxZone } from "./types.js";

// ---------------------------------------------------------------------------
// Sätze
// ---------------------------------------------------------------------------

export type RateCategory = "standard" | "reduced" | "special";

/** USt/MWST-Sätze in Prozent. CH „special“ = Beherbergung. */
export const VAT_RATES: Readonly<Record<string, Readonly<Partial<Record<RateCategory, number>>>>> = {
  DE: { standard: 19, reduced: 7 },
  AT: { standard: 20, reduced: 10, special: 13 },
  CH: { standard: 8.1, reduced: 2.6, special: 3.8 },
  // Liechtenstein bildet mit der Schweiz ein Mehrwertsteuergebiet
  LI: { standard: 8.1, reduced: 2.6, special: 3.8 },
};

/**
 * B.1 (Schweiz-Plan 14.10.2026): Steuersätze mit Gültigkeitsbeginn, massgeblich ist das Leistungsdatum. Ein Land ohne Eintrag
 * nimmt VAT_RATES. Für die Schweiz liegt eine mögliche Erhöhung ab 2028 vor (Volksabstimmung 29.11.2026); erst nach dem
 * Beschluss hier einen weiteren Zeitraum anfügen, z. B. { from: "2028-01-01", rates: { standard: 8.5, reduced: 2.6, special: 4.0 } }.
 */
export const VAT_RATE_PERIODS: Readonly<Record<string, readonly { from: string; rates: Readonly<Partial<Record<RateCategory, number>>> }[]>> = {
  CH: [{ from: "2024-01-01", rates: { standard: 8.1, reduced: 2.6, special: 3.8 } }],
  LI: [{ from: "2024-01-01", rates: { standard: 8.1, reduced: 2.6, special: 3.8 } }],
};

/** Steuersätze eines Landes am Leistungsdatum (ohne Datum: heute geltende Sätze) */
export function vatRatesOn(country: string | null | undefined, serviceDate?: string | null): Readonly<Partial<Record<RateCategory, number>>> | undefined {
  const c = normalizeCountry(country ?? null);
  if (!c) return undefined;
  const periods = VAT_RATE_PERIODS[c];
  if (!periods?.length || !serviceDate) return VAT_RATES[c];
  const day = serviceDate.slice(0, 10);
  const hit = [...periods].filter((p) => p.from <= day).sort((a, b) => b.from.localeCompare(a.from))[0];
  return hit?.rates ?? VAT_RATES[c];
}

/**
 * Steuerprüfung L12, Abnahme E-2: wählbare Steuersätze je Position in Basispunkten, absteigend. Die Sätze des Landes am
 * Leistungsdatum (DE 19/7, AT 20/13/10, CH/LI 8,1/3,8/2,6), dazu der Satz des Steuerfalls und 0 %. Ohne Steuerausweis
 * (Kleinunternehmer, Reverse Charge, nicht steuerbar: Satz 0) keine Auswahl.
 */
export function vatRateChoices(country: string | null | undefined, standardPercent: number, serviceDate?: string | null): number[] {
  if (!(standardPercent > 0)) return [];
  const known = vatRatesOn(country ?? null, serviceDate ?? null);
  const list = [standardPercent, ...(known ? Object.values(known).filter((x): x is number => typeof x === "number") : []), 0];
  return [...new Set(list.map((x) => Math.round(x * 100)))].sort((a, b) => b - a);
}

// ---------------------------------------------------------------------------
// Regeln als Daten
// ---------------------------------------------------------------------------

export type TaxMode =
  | "DOMESTIC"
  | "SMALL_BUSINESS"
  | "REVERSE_CHARGE_DOMESTIC"
  | "REVERSE_CHARGE_EU"
  | "NOT_TAXABLE_ABROAD"
  | "MANUAL"
  /** 0198: Beleg über Verzugskosten (Pauschale, Zinsen) – Schadensersatz, nicht umsatzsteuerbar; nur über invoice_create_cost_note */
  | "NOT_TAXABLE_DAMAGES";

/** Gespeicherter Wert in invoices.tax_mode (ein Wertevorrat für Generator, Creator-Portal und Datenbank, 0044). */
export const TAX_MODE_DB: Record<TaxMode, string> = {
  DOMESTIC: "standard",
  SMALL_BUSINESS: "small_business",
  REVERSE_CHARGE_DOMESTIC: "reverse_charge_domestic",
  REVERSE_CHARGE_EU: "reverse_charge_eu",
  NOT_TAXABLE_ABROAD: "not_taxable_abroad",
  MANUAL: "manual",
  NOT_TAXABLE_DAMAGES: "not_taxable_damages",
};

/** Liest tax_mode aus der Datenbank (auch Altwerte in Großbuchstaben oder „reverse_charge“). */
export function taxModeFromDb(v: string | null | undefined): TaxMode | null {
  if (!v) return null;
  const up = v.toUpperCase();
  if (up in TAX_MODE_DB) return up as TaxMode;
  const found = (Object.keys(TAX_MODE_DB) as TaxMode[]).find((k) => TAX_MODE_DB[k] === v.toLowerCase());
  if (found) return found;
  if (v.toLowerCase() === "reverse_charge") return "REVERSE_CHARGE_EU";
  return null;
}

const TAX_MODE_LABEL: Record<TaxMode, { de: string; en: string }> = {
  DOMESTIC: { de: "Regelbesteuerung (Umsatzsteuer ausgewiesen)", en: "Standard VAT" },
  SMALL_BUSINESS: { de: "Kleinunternehmer (ohne Umsatzsteuer)", en: "Small business (no VAT)" },
  REVERSE_CHARGE_DOMESTIC: { de: "Steuerschuldnerschaft des Leistungsempfängers (Inland)", en: "Domestic reverse charge" },
  REVERSE_CHARGE_EU: { de: "Reverse Charge (EU)", en: "Reverse charge (EU)" },
  NOT_TAXABLE_ABROAD: { de: "Nicht im Inland steuerbar (Leistungsort Ausland)", en: "Not taxable domestically (place of supply abroad)" },
  MANUAL: { de: "Manuell festgelegter Steuerfall", en: "Manually set tax treatment" },
  NOT_TAXABLE_DAMAGES: { de: "Nicht steuerbar (Schadensersatz: Verzugspauschale, Verzugszinsen)", en: "Not subject to VAT (damages: late payment compensation, default interest)" },
};
export function taxModeLabel(mode: TaxMode | null, lang: Language = "de"): string {
  return mode ? TAX_MODE_LABEL[mode][lang] : lang === "en" ? "unknown" : "unbekannt";
}

/**
 * Steuerfälle, die CreatorOffice nur vorschlägt (Anwalt Punkt 11 und 23): Außer „Inland, regulär besteuert“ und „Kleinunternehmer
 * laut eigenem Profil“ bestätigt der Aussteller die steuerliche Behandlung ausdrücklich, bevor die Rechnung festgeschrieben bzw.
 * eingereicht wird. Grundlage bleibt das Steuerprofil (Sitzland, Steuerstatus, USt-IdNr.) und der Empfänger; die Datenbank prüft
 * dieselbe Liste beim Festschreiben (0181, invoices.tax_treatment_confirmed_at).
 */
export const TAX_MODES_NEEDING_CONFIRMATION: readonly TaxMode[] = ["REVERSE_CHARGE_DOMESTIC", "REVERSE_CHARGE_EU", "NOT_TAXABLE_ABROAD", "MANUAL"];

/**
 * Braucht der Steuerfall dieser Rechnung eine ausdrückliche Bestätigung? Ja bei Reverse Charge, nicht steuerbar im Ausland und
 * manueller Prüfung; ebenso, wenn von Hand ein anderer Fall als der Vorschlag aus Profil und Empfänger gewählt ist (auch „Inland“
 * oder „Kleinunternehmer“ entgegen dem Profil) oder die Regel selbst eine manuelle Prüfung verlangt.
 */
export function taxTreatmentNeedsConfirmation(input: { mode: TaxMode | null; suggested?: TaxMode | null; manualReview?: boolean }): boolean {
  const mode = input.mode ?? "MANUAL";
  if (TAX_MODES_NEEDING_CONFIRMATION.includes(mode)) return true;
  if (input.manualReview) return true;
  return !!input.suggested && input.suggested !== mode;
}

/** Beziehung Empfängerland ↔ Ausstellerland. */
export type RecipientRelation = "same" | "DE" | "eu_other" | "foreign" | "any";

export interface IssuerMatch {
  /** konkrete Länder */
  countries?: CountryCode[];
  /** oder Gruppe */
  group?: "EU" | "ANY";
  /** Aussteller ausgeschlossen */
  except?: CountryCode[];
  /** Aussteller aus dieser Gruppe ausgeschlossen (z. B. alle EU-Länder) */
  exceptGroup?: "EU";
}

export interface TaxRule {
  id: string;
  issuer: IssuerMatch;
  issuerSmallBusiness?: boolean;
  recipient: RecipientRelation;
  /** Empfänger nur in diesen Ländern (zusätzlich zur Beziehung) */
  recipientIn?: CountryCode[];
  recipientIsBusiness?: boolean;
  mode: TaxMode;
  /** 'domestic' = Satz des Ausstellerlands nach Kategorie, 'zero' = 0 % */
  rate: "domestic" | "zero";
  noticeKey: string;
  requiredFields: string[];
  manualReview?: boolean;
}

/** Pflichtfeld-Schlüssel (für requiredFields/missingFields). */
export const F = {
  issuerVatId: "issuerVatId",
  recipientVatId: "recipientVatId",
  issuerTaxNumberOrVatId: "issuerTaxNumberOrVatId",
  issuerChUid: "issuerChUid",
  serviceDate: "serviceDate",
  chQrIban: "chQrBill.iban",
  chQrCreditor: "chQrBill.creditorNameAndAddress",
  chQrAmount: "chQrBill.amountAndCurrency",
  chQrReference: "chQrBill.reference",
} as const;

/** Standard-Regelwerk DACH, in Auswertungsreihenfolge (erster Treffer gewinnt). */
export const DEFAULT_TAX_RULES: readonly TaxRule[] = [
  { id: "DE_SMALL", issuer: { countries: ["DE"] }, issuerSmallBusiness: true, recipient: "same", mode: "SMALL_BUSINESS", rate: "zero", noticeKey: "DE_SMALL_BUSINESS", requiredFields: [] },
  { id: "AT_SMALL", issuer: { countries: ["AT"] }, issuerSmallBusiness: true, recipient: "same", mode: "SMALL_BUSINESS", rate: "zero", noticeKey: "AT_SMALL_BUSINESS", requiredFields: [] },
  { id: "CH_SMALL", issuer: { countries: ["CH"] }, issuerSmallBusiness: true, recipient: "same", mode: "SMALL_BUSINESS", rate: "zero", noticeKey: "CH_NOT_VAT_REGISTERED", requiredFields: [] },
  { id: "DE_DOMESTIC", issuer: { countries: ["DE"] }, recipient: "same", mode: "DOMESTIC", rate: "domestic", noticeKey: "DE_DOMESTIC_VAT", requiredFields: [] },
  { id: "AT_DOMESTIC", issuer: { countries: ["AT"] }, recipient: "same", mode: "DOMESTIC", rate: "domestic", noticeKey: "AT_DOMESTIC_VAT", requiredFields: [] },
  { id: "CH_DOMESTIC", issuer: { countries: ["CH"] }, recipient: "same", mode: "DOMESTIC", rate: "domestic", noticeKey: "CH_DOMESTIC_VAT", requiredFields: [] },
  // Schweiz und Liechtenstein: ein Mehrwertsteuergebiet (Leistungen untereinander wie im Inland)
  { id: "CH_LI_SMALL", issuer: { countries: ["CH", "LI"] }, issuerSmallBusiness: true, recipient: "any", recipientIn: ["CH", "LI"], mode: "SMALL_BUSINESS", rate: "zero", noticeKey: "CH_NOT_VAT_REGISTERED", requiredFields: [] },
  { id: "CH_LI_DOMESTIC", issuer: { countries: ["CH", "LI"] }, recipient: "any", recipientIn: ["CH", "LI"], mode: "DOMESTIC", rate: "domestic", noticeKey: "CH_DOMESTIC_VAT", requiredFields: [] },
  // EU-Aussteller an Unternehmen in einem anderen EU-Land (auch Deutschland): innergemeinschaftliches Reverse Charge
  { id: "EU_RC", issuer: { group: "EU" }, recipient: "eu_other", recipientIsBusiness: true, mode: "REVERSE_CHARGE_EU", rate: "zero", noticeKey: "EU_REVERSE_CHARGE", requiredFields: [F.recipientVatId, F.issuerVatId] },
  // K-453 (Entscheidung 16.10.2026): Aussteller in der Schweiz bzw. in Liechtenstein an Unternehmen in der EU: zusätzlich der
  // Satz, dass die Leistung dort nicht der MWST unterliegt (Leistungsort beim Empfänger, Art. 8 Abs. 1 MWSTG)
  { id: "CH_TO_DE_RC_13B", issuer: { countries: ["CH", "LI"] }, recipient: "DE", recipientIsBusiness: true, mode: "REVERSE_CHARGE_DOMESTIC", rate: "zero", noticeKey: "CH_TO_DE_REVERSE_CHARGE_13B", requiredFields: [] },
  // Aussteller außerhalb der EU an deutsche Unternehmen: § 13b UStG
  { id: "DE_RC_13B", issuer: { group: "ANY", except: ["DE"], exceptGroup: "EU" }, recipient: "DE", recipientIsBusiness: true, mode: "REVERSE_CHARGE_DOMESTIC", rate: "zero", noticeKey: "DE_REVERSE_CHARGE_13B", requiredFields: [] },
  { id: "CH_TO_EU_RC", issuer: { countries: ["CH", "LI"] }, recipient: "eu_other", recipientIsBusiness: true, mode: "REVERSE_CHARGE_EU", rate: "zero", noticeKey: "CH_TO_EU_REVERSE_CHARGE", requiredFields: [F.recipientVatId, F.issuerVatId] },
  // K-270/K-289 (Schweiz-Prüfung 14.10.2026): Leistung aus der EU an ein Unternehmen in der Schweiz bzw. in Liechtenstein: im
  // eigenen Land nicht steuerbar, in CH/LI schuldet der Empfänger die Bezugsteuer (Art. 45 MWSTG); auch Provisionen einer DE/AT-Agentur
  // an einen Creator in der Schweiz
  { id: "EU_TO_CH_ACQUISITION", issuer: { group: "EU" }, recipient: "foreign", recipientIn: ["CH", "LI"], recipientIsBusiness: true, mode: "NOT_TAXABLE_ABROAD", rate: "zero", noticeKey: "ABROAD_CH_ACQUISITION_TAX", requiredFields: [] },
  { id: "DACH_ABROAD", issuer: { countries: ["DE", "AT", "CH", "LI"] }, recipient: "foreign", recipientIsBusiness: true, mode: "NOT_TAXABLE_ABROAD", rate: "zero", noticeKey: "THIRD_COUNTRY_NOT_TAXABLE", requiredFields: [] },
  // K-270: Werbe- und Medienleistungen aus CH/LI an Privatpersonen im Ausland: Leistungsort beim Empfänger (Art. 8 Abs. 1 MWSTG),
  // in der Schweiz nicht steuerbar
  { id: "CH_ABROAD_PRIVATE", issuer: { countries: ["CH", "LI"] }, recipient: "foreign", recipientIsBusiness: false, mode: "NOT_TAXABLE_ABROAD", rate: "zero", noticeKey: "THIRD_COUNTRY_NOT_TAXABLE", requiredFields: [] },
  // Aussteller aus Drittländern (z. B. Freezone-Agentur): an EU-Unternehmen Reverse Charge, an CH/LI Bezugsteuer beim Empfänger.
  // 0444 (Steuerprüfung L7): keine Pflicht zur USt-IdNr. des Ausstellers; ohne Registrierung in der EU hat er keine (Art. 214
  // MwStSystRL). Statt dessen Hinweis ISSUER_NO_EU_VAT_ID und beim Festschreiben die Rückfrage „keine EU-Registrierung“.
  { id: "FOREIGN_TO_EU_RC", issuer: { group: "ANY", except: ["CH", "LI"], exceptGroup: "EU" }, recipient: "eu_other", recipientIsBusiness: true, mode: "REVERSE_CHARGE_EU", rate: "zero", noticeKey: "EU_REVERSE_CHARGE", requiredFields: [F.recipientVatId] },
  { id: "FOREIGN_TO_CH", issuer: { group: "ANY", except: ["CH", "LI"], exceptGroup: "EU" }, recipient: "foreign", recipientIn: ["CH", "LI"], recipientIsBusiness: true, mode: "REVERSE_CHARGE_DOMESTIC", rate: "zero", noticeKey: "CH_ACQUISITION_TAX", requiredFields: [] },
];

/**
 * Von Hand gewählter Steuerfall (Generator): Satz immer aus dem Land des Ausstellers, Hinweis passend zu Fall und Land.
 * Kein Steuerbetrag mit einem Reverse-Charge- oder Auslandshinweis.
 */
export function forcedTaxTreatment(mode: Exclude<TaxMode, "MANUAL">, input: { issuerCountry: string | null; recipientCountry: string | null; rateCategory?: RateCategory; serviceDate?: string | null }): { ratePercent: number; noticeKey: string } {
  const issuer = normalizeCountry(input.issuerCountry);
  const recipient = normalizeCountry(input.recipientCountry);
  // K-453: Aussteller in der Schweiz bzw. in Liechtenstein an ein EU-Land: Hinweis mit dem Satz zur Schweizer MWST
  const chIssuer = (issuer === "CH" || issuer === "LI") && recipient !== "CH" && recipient !== "LI";
  switch (mode) {
    case "DOMESTIC": {
      const table = issuer ? vatRatesOn(issuer, input.serviceDate) : undefined;
      const rate = table?.[input.rateCategory ?? "standard"] ?? table?.standard;
      if (!issuer || rate === undefined) return { ratePercent: 0, noticeKey: "MANUAL_REVIEW" };
      const key = issuer === "LI" ? "CH_DOMESTIC_VAT" : `${issuer}_DOMESTIC_VAT`;
      return { ratePercent: clampRatePercent(rate), noticeKey: TAX_NOTICES[key] ? key : "MANUAL_REVIEW" };
    }
    case "SMALL_BUSINESS":
      return { ratePercent: 0, noticeKey: issuer === "AT" ? "AT_SMALL_BUSINESS" : issuer === "CH" || issuer === "LI" ? "CH_NOT_VAT_REGISTERED" : issuer === "DE" ? "DE_SMALL_BUSINESS" : "MANUAL_REVIEW" };
    case "REVERSE_CHARGE_EU":
      return { ratePercent: 0, noticeKey: chIssuer ? "CH_TO_EU_REVERSE_CHARGE" : "EU_REVERSE_CHARGE" };
    case "REVERSE_CHARGE_DOMESTIC":
      return { ratePercent: 0, noticeKey: recipient === "CH" || recipient === "LI" ? "CH_ACQUISITION_TAX"
        : recipient === "DE" ? (chIssuer ? "CH_TO_DE_REVERSE_CHARGE_13B" : "DE_REVERSE_CHARGE_13B") : chIssuer ? "CH_TO_EU_REVERSE_CHARGE" : "EU_REVERSE_CHARGE" };
    case "NOT_TAXABLE_ABROAD":
      return { ratePercent: 0, noticeKey: "THIRD_COUNTRY_NOT_TAXABLE" };
    case "NOT_TAXABLE_DAMAGES":
      // K-312: Hinweis je Land
      return { ratePercent: 0, noticeKey: issuer === "AT" ? "AT_DAMAGES_NOT_TAXABLE" : issuer === "CH" || issuer === "LI" ? "CH_DAMAGES_NOT_TAXABLE" : "DE_DAMAGES_NOT_TAXABLE" };
  }
}

/** Steuernummer-Pflicht (WS-07) als Daten. Erster Treffer gewinnt, kein Treffer = keine Pflicht. */
export interface TaxIdRequirementRule {
  id: string;
  issuer: CountryCode[];
  issuerSmallBusiness?: boolean;
  recipient: RecipientRelation;
  /** nur über der Kleinbetragsgrenze */
  aboveSmallAmount?: boolean;
  /** nur über diesem Bruttobetrag in dieser Währung (SAAS-7: AT über 10.000 € brutto) */
  aboveGross?: { currency: Currency; cents: Cents };
  /** nur, wenn der Empfänger ausdrücklich Unternehmer ist */
  recipientBusiness?: boolean;
  /** Empfänger nur in diesen Ländern (zusätzlich zur Beziehung, wie TaxRule.recipientIn) */
  recipientIn?: CountryCode[];
  fields: string[];
}

export const DEFAULT_TAX_ID_RULES: readonly TaxIdRequirementRule[] = [
  { id: "DE_EU_BOTH_VAT_IDS", issuer: ["DE"], recipient: "eu_other", fields: [F.issuerVatId, F.recipientVatId] },
  // Steuerprüfung 23.10.2026: auch die Rechnung eines Kleinunternehmers nennt Steuernummer oder USt-IdNr. (§ 34a Satz 1 Nr. 2
  // UStDV seit 01.01.2025); ausgenommen nur die Kleinbetragsrechnung bis 250 € (§ 34a Satz 2 i. V. m. § 33 UStDV)
  { id: "DE_SMALL_DOMESTIC", issuer: ["DE"], issuerSmallBusiness: true, recipient: "same", aboveSmallAmount: true, fields: [F.issuerTaxNumberOrVatId] },
  { id: "DE_DOMESTIC", issuer: ["DE"], recipient: "same", aboveSmallAmount: true, fields: [F.issuerTaxNumberOrVatId] },
  { id: "AT_EU_BOTH_VAT_IDS", issuer: ["AT"], recipient: "eu_other", fields: [F.issuerVatId, F.recipientVatId] },
  // SAAS-7: § 11 Abs. 1 Z 3 lit. b UStG (AT): über 10.000 € brutto an einen Unternehmer im Inland auch die UID des Empfängers
  // (lit. i ist die UID des Ausstellers; Prüfung Verträge/Steuer 07.10.2026, Fundstelle korrigiert 14.10.2026)
  { id: "AT_DOMESTIC_ABOVE_10K", issuer: ["AT"], issuerSmallBusiness: false, recipient: "same", aboveGross: { currency: "EUR", cents: 1_000_000 }, recipientBusiness: true,
    fields: [F.issuerVatId, F.recipientVatId] },
  // K-249: § 11 Abs. 1 Z 3 lit. i UStG (AT) – UID des Ausstellers ab 400 € brutto (darunter Kleinbetragsrechnung § 11 Abs. 6);
  // eine Steuernummer ersetzt sie in Österreich nicht, Kleinunternehmer (§ 6 Abs. 1 Z 27) brauchen keine
  { id: "AT_DOMESTIC", issuer: ["AT"], issuerSmallBusiness: false, recipient: "same", aboveSmallAmount: true, fields: [F.issuerVatId] },
  { id: "CH_DOMESTIC", issuer: ["CH"], issuerSmallBusiness: false, recipient: "same", fields: [F.issuerChUid] },
  // Art. 26 Abs. 2 lit. a MWSTG (und das gleichlautende MWSTG Liechtensteins): Schweiz und Liechtenstein sind ein
  // Mehrwertsteuergebiet (Steuerregeln CH_LI_DOMESTIC); auch Rechnungen zwischen beiden Ländern und Inlandsrechnungen aus
  // Liechtenstein nennen die Nummer, unter der der Aussteller eingetragen ist (Prüfung Verträge/Steuer 07.10.2026).
  { id: "CH_TO_LI", issuer: ["CH"], issuerSmallBusiness: false, recipient: "foreign", recipientIn: ["LI"], fields: [F.issuerChUid] },
  { id: "LI_DOMESTIC", issuer: ["LI"], issuerSmallBusiness: false, recipient: "any", recipientIn: ["CH", "LI"], fields: [F.issuerVatId] },
  // Gleich zur Steuerregel CH_TO_EU_RC: Reverse Charge braucht die USt-IdNr. des Empfängers (WS-07, R-16)
  { id: "CH_EU_RECIPIENT_VAT_ID", issuer: ["CH"], recipient: "eu_other", fields: [F.recipientVatId] },
];

export interface SmallAmountInvoiceRule {
  issuerCountry: CountryCode;
  currency: Currency;
  maxGrossCents: Cents;
}

export interface TaxConfig {
  rules: readonly TaxRule[];
  taxIdRules: readonly TaxIdRequirementRule[];
  /** Kleinbetragsrechnung (WS-12): gilt NUR für dieses Ausstellerland und diese Währung */
  smallAmountInvoice: SmallAmountInvoiceRule | null;
  /** K-250: weitere Länder mit eigener Kleinbetragsgrenze (AT § 11 Abs. 6 UStG: 400 € brutto) */
  smallAmountInvoices?: readonly SmallAmountInvoiceRule[];
  /** Währungen, für die bei Ausstellern in CH die QR-Rechnung verlangt wird */
  chQrBillCurrencies: Currency[];
}

export const DEFAULT_TAX_CONFIG: TaxConfig = {
  rules: DEFAULT_TAX_RULES,
  taxIdRules: DEFAULT_TAX_ID_RULES,
  smallAmountInvoice: { issuerCountry: "DE", currency: "EUR", maxGrossCents: 25_000 },
  smallAmountInvoices: [{ issuerCountry: "AT", currency: "EUR", maxGrossCents: 40_000 }],
  chQrBillCurrencies: ["CHF", "EUR"],
};

// ---------------------------------------------------------------------------
// Rechtstexte (Übersetzungstabelle)
// ---------------------------------------------------------------------------

export type NoticeTable = Readonly<Record<string, Readonly<Record<Language, string>>>>;

export const TAX_NOTICES: NoticeTable = {
  // Inhaber 14.10.2026: Hinweis auf die Steuerbefreiung nach der Rechtslage seit 2025 (§ 34a UStDV); festgeschriebene Rechnungen
  // behalten ihren Text
  DE_SMALL_BUSINESS: {
    de: "Umsatzsteuerbefreit nach § 19 Abs. 1 UStG (Kleinunternehmerregelung).",
    en: "VAT exempt under section 19(1) UStG (small business scheme).",
  },
  AT_SMALL_BUSINESS: {
    de: "Umsatzsteuerbefreit – Kleinunternehmer gemäß § 6 Abs. 1 Z 27 UStG.",
    en: "VAT exempt – small business (Section 6 (1) no. 27 Austrian VAT Act).",
  },
  CH_NOT_VAT_REGISTERED: {
    de: "Nicht mehrwertsteuerpflichtig.",
    en: "Not registered for Swiss VAT.",
  },
  // CREATOR-R2-10: Der Hinweis steht unter dem Brutto-Gesamtbetrag; „zzgl.“ ließe glauben, die Steuer komme noch dazu
  DE_DOMESTIC_VAT: { de: "Enthält {rate} % Umsatzsteuer.", en: "Includes {rate}% German VAT." },
  AT_DOMESTIC_VAT: { de: "Enthält {rate} % Umsatzsteuer.", en: "Includes {rate}% Austrian VAT." },
  CH_DOMESTIC_VAT: { de: "Enthält {rate} % MWST.", en: "Includes {rate}% Swiss VAT." },
  DE_REVERSE_CHARGE_13B: {
    de: "Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG).",
    en: "Reverse charge: the recipient is liable for VAT (Section 13b German VAT Act).",
  },
  // K-453: Aussteller in der Schweiz bzw. in Liechtenstein
  CH_TO_DE_REVERSE_CHARGE_13B: {
    de: "Leistung im Inland nicht der MWST unterstellt (Leistungsort beim Empfänger, Art. 8 Abs. 1 MWSTG). Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG).",
    en: "Not subject to Swiss VAT (place of supply at the recipient, Art. 8(1) Swiss VAT Act). Reverse charge: the recipient is liable for VAT (Section 13b German VAT Act).",
  },
  CH_TO_EU_REVERSE_CHARGE: {
    de: "Leistung im Inland nicht der MWST unterstellt (Leistungsort beim Empfänger, Art. 8 Abs. 1 MWSTG). Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge, Art. 44 und 196 MwStSystRL).",
    en: "Not subject to Swiss VAT (place of supply at the recipient, Art. 8(1) Swiss VAT Act). Reverse charge: VAT is payable by the recipient (Art. 44 and 196 Directive 2006/112/EC).",
  },
  EU_REVERSE_CHARGE: {
    de: "Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge, Art. 44 und 196 MwStSystRL).",
    en: "Reverse charge: VAT is payable by the recipient (Art. 44 and 196 Directive 2006/112/EC).",
  },
  CH_ACQUISITION_TAX: {
    de: "Leistung aus dem Ausland: Die Mehrwertsteuer schuldet der Empfänger (Bezugsteuer, Art. 45 MWSTG).",
    en: "Service from abroad: VAT is payable by the recipient (acquisition tax, Art. 45 Swiss VAT Act).",
  },
  // K-270/K-289: Aussteller in der EU, Empfänger ein Unternehmen in der Schweiz bzw. in Liechtenstein
  ABROAD_CH_ACQUISITION_TAX: {
    de: "Im Inland nicht steuerbare Leistung; Leistungsort ist der Sitz des Empfängers in der Schweiz bzw. in Liechtenstein. Die Mehrwertsteuer schuldet dort der Empfänger (Bezugsteuer, Art. 45 MWSTG).",
    en: "Not taxable in the issuer's country; the place of supply is the recipient's seat in Switzerland or Liechtenstein. VAT is payable there by the recipient (acquisition tax, Art. 45 Swiss VAT Act).",
  },
  THIRD_COUNTRY_NOT_TAXABLE: {
    de: "Im Inland nicht steuerbare Leistung; Leistungsort ist das Land des Empfängers.",
    en: "Not taxable in the issuer's country; the place of supply is the recipient's country.",
  },
  // 0198: Beleg über Verzugskosten
  DE_DAMAGES_NOT_TAXABLE: {
    de: "Verzugspauschale und Verzugszinsen sind Schadensersatz und unterliegen nicht der Umsatzsteuer.",
    en: "Late payment compensation and default interest are damages and are not subject to VAT.",
  },
  // K-312: Verzugskosten in Österreich (Pauschale nach § 458 UGB) und in der Schweiz/Liechtenstein (kein Entgelt, Art. 18 Abs. 2 lit. i MWSTG)
  AT_DAMAGES_NOT_TAXABLE: {
    de: "Pauschale für Betreibungskosten, Mahnspesen und Verzugszinsen sind Schadenersatz und unterliegen nicht der Umsatzsteuer.",
    en: "Flat compensation for recovery costs, dunning charges and default interest are damages and are not subject to VAT.",
  },
  CH_DAMAGES_NOT_TAXABLE: {
    de: "Verzugszinsen und Betreibungskosten sind kein Entgelt und unterliegen nicht der MWST.",
    en: "Default interest and debt collection costs are not consideration and are not subject to Swiss VAT.",
  },
  MANUAL_REVIEW: {
    de: "Steuerliche Behandlung bitte manuell prüfen.",
    en: "Tax treatment requires manual review.",
  },
};

/** ST-04: Rechtstext zu einem Schlüssel; eine Agentur kann eigene Texte überlagern. */
export function renderTaxNotice(
  noticeKey: string,
  lang: Language,
  params: Record<string, string | number> = {},
  overrides?: Partial<Record<string, Partial<Record<Language, string>>>>,
): string {
  const tpl = overrides?.[noticeKey]?.[lang] ?? TAX_NOTICES[noticeKey]?.[lang] ?? noticeKey;
  return tpl.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = params[k];
    if (v === undefined) return `{${k}}`;
    return typeof v === "number" && lang === "de" ? String(v).replace(".", ",") : String(v);
  });
}

/**
 * Steuerhinweis für Positionen mit mehreren Sätzen (Steuerprüfung L12, Abnahme E-7): aus „Enthält {rate} % Umsatzsteuer.“ wird
 * „Enthält Umsatzsteuer (19 % und 7 %).“, englisch „Includes German VAT (19% and 7%).“. Gezählt werden die Sätze über 0 %; bei
 * einem Satz (oder einem Hinweis ohne Satz) wie renderTaxNotice.
 */
export function renderTaxNoticeForRates(
  noticeKey: string,
  lang: Language,
  ratesPercent: readonly number[],
  overrides?: Partial<Record<string, Partial<Record<Language, string>>>>,
): string {
  const rates = [...new Set(ratesPercent.filter((r) => r > 0).map((r) => Math.round(r * 100) / 100))].sort((a, b) => b - a);
  const tpl = overrides?.[noticeKey]?.[lang] ?? TAX_NOTICES[noticeKey]?.[lang] ?? noticeKey;
  if (rates.length < 2 || !tpl.includes("{rate}")) return renderTaxNotice(noticeKey, lang, { rate: rates[0] ?? 0 }, overrides);
  const one = (r: number) => (lang === "de" ? `${String(r).replace(".", ",")} %` : `${r}%`);
  const and = lang === "de" ? "und" : "and";
  const list = `${rates.slice(0, -1).map(one).join(", ")} ${and} ${one(rates[rates.length - 1]!)}`;
  const base = tpl.replace(/\{rate\}\s?%\s?/, "").trim();
  return base.endsWith(".") ? `${base.slice(0, -1)} (${list}).` : `${base} (${list})`;
}

// ---------------------------------------------------------------------------
// Bezeichnung der Steuer-ID je Land (ST-11)
// ---------------------------------------------------------------------------

export const TAX_ID_LABELS: Readonly<Record<string, Readonly<Record<Language, string>>>> = {
  DE: { de: "USt-IdNr.", en: "VAT ID" },
  AT: { de: "UID-Nr.", en: "VAT ID (UID)" },
  CH: { de: "UID/MWST-Nr.", en: "UID/VAT No." },
  LI: { de: "MWST-Nr.", en: "VAT No." },
  GB: { de: "VAT-Nr.", en: "VAT No." },
  AE: { de: "TRN", en: "TRN" },
  US: { de: "EIN", en: "EIN" },
  HK: { de: "BRN", en: "BRN" },
  NO: { de: "MVA-Nr.", en: "VAT No. (MVA)" },
};
const EU_DEFAULT_LABEL = { de: "USt-IdNr.", en: "VAT ID" } as const;
const NEUTRAL_LABEL = { de: "Steuernummer/Tax ID", en: "Tax ID" } as const;

/** Die Bezeichnung folgt dem Land des Beteiligten, nicht der Rechnungssprache. */
export function taxIdLabel(
  country: string | null,
  lang: Language,
  overrides?: Partial<Record<string, Partial<Record<Language, string>>>>,
): string {
  const code = normalizeCountry(country);
  if (!code) return NEUTRAL_LABEL[lang];
  const o = overrides?.[code]?.[lang];
  if (o) return o;
  const t = TAX_ID_LABELS[code];
  if (t) return t[lang];
  if (isEuCountry(code)) return EU_DEFAULT_LABEL[lang];
  return NEUTRAL_LABEL[lang];
}

// ---------------------------------------------------------------------------
// Formatprüfungen (ST-09)
// ---------------------------------------------------------------------------

/**
 * 0444 (Steuerprüfung L7): Ist das eine USt-IdNr. eines EU-Mitgliedstaats (Länderpräfix nach Art. 215 MwStSystRL, EL für
 * Griechenland, XI für Nordirland)? Wie app.is_eu_vat_id in der Datenbank. Eine Steuernummer aus einem Drittland (z. B. TRN) ist keine.
 */
export function isEuVatIdFormat(value: string | null | undefined): boolean {
  const v = (value ?? "").replace(/[\s.\-]/g, "").toUpperCase();
  return /^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK|XI)[0-9A-Z+*]{2,12}$/.test(v);
}

/** Formathinweis zu einer USt-IdNr. Ergebnis ist nie eine Sperre. */
export function checkVatIdFormat(value: string | null | undefined, ownerCountry: string | null): Finding | null {
  if (!value || !value.trim()) return null;
  const v = value.replace(/[\s.]/g, "").toUpperCase();
  const country = normalizeCountry(ownerCountry);
  if (country === "DE") {
    if (/^DE\d{9}$/.test(v)) return null;
    if (value.includes("/") || /^\d{10,13}$/.test(v)) return { code: "VAT_ID_LOOKS_LIKE_TAX_NUMBER", severity: "question" };
    return { code: "VAT_ID_FORMAT", severity: "hint", params: { expected: "DE + 9 Ziffern" } };
  }
  if (country === "AT") return /^ATU\d{8}$/.test(v) ? null : { code: "VAT_ID_FORMAT", severity: "hint", params: { expected: "ATU + 8 Ziffern" } };
  if (country === "CH") return /^CHE-?\d{9}(MWST|TVA|IVA)?$/.test(v) ? null : { code: "VAT_ID_FORMAT", severity: "hint", params: { expected: "CHE-123.456.789 MWST" } };
  if (country && isEuCountry(country)) {
    return /^[A-Z]{2}[0-9A-Z+*]{2,13}$/.test(v) ? null : { code: "VAT_ID_MUST_START_WITH_COUNTRY", severity: "hint" };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Kleinbetragsrechnung (WS-12) und Steuernummer-Pflicht (WS-07)
// ---------------------------------------------------------------------------

/** Alle Kleinbetragsgrenzen (DE § 33 UStDV, AT § 11 Abs. 6 UStG) */
export function smallAmountRules(config: TaxConfig = DEFAULT_TAX_CONFIG): SmallAmountInvoiceRule[] {
  return [...(config.smallAmountInvoice ? [config.smallAmountInvoice] : []), ...(config.smallAmountInvoices ?? [])];
}

export function isSmallAmountInvoice(
  input: { issuerCountry: string | null; currency: Currency | null; grossCents: Cents },
  config: TaxConfig = DEFAULT_TAX_CONFIG,
): boolean {
  const country = normalizeCountry(input.issuerCountry);
  return smallAmountRules(config).some((r) => country === r.issuerCountry && input.currency === r.currency && input.grossCents <= r.maxGrossCents);
}

function relationMatches(rel: RecipientRelation, issuer: CountryCode, recipient: CountryCode): boolean {
  switch (rel) {
    case "any":
      return true;
    case "same":
      return issuer === recipient;
    case "DE":
      return recipient === "DE" && issuer !== "DE";
    case "eu_other":
      return recipient !== issuer && isEuCountry(recipient);
    case "foreign":
      return recipient !== issuer;
  }
}

export interface TaxIdRequirementInput {
  issuerCountry: string | null;
  issuerSmallBusiness: boolean;
  recipientCountry: string | null;
  grossCents: Cents;
  /** null = unbekannte Währung: keine Kleinbetragsregel */
  currency: Currency | null;
  /** Empfänger ist Unternehmer (für Regeln mit recipientBusiness; unbekannt = Regel greift nicht) */
  recipientIsBusiness?: boolean;
}

export interface TaxIdRequirement {
  fields: string[];
  ruleId: string | null;
  /** immer 'hint': fehlende Nummern sind nie eine Sperre (ST-08) */
  severity: "hint";
}

/**
 * WS-07: EINE Regel für die Steuernummer-Pflicht. Generator, Profil und Anforderungsprüfung
 * rufen alle diese Funktion.
 */
export function taxIdRequirement(input: TaxIdRequirementInput, config: TaxConfig = DEFAULT_TAX_CONFIG): TaxIdRequirement {
  const issuer = normalizeCountry(input.issuerCountry);
  const recipient = normalizeCountry(input.recipientCountry);
  if (!issuer || !recipient) return { fields: [], ruleId: null, severity: "hint" };
  const small = isSmallAmountInvoice({ issuerCountry: issuer, currency: input.currency, grossCents: input.grossCents }, config);
  for (const r of config.taxIdRules) {
    if (!r.issuer.includes(issuer)) continue;
    if (r.issuerSmallBusiness !== undefined && r.issuerSmallBusiness !== input.issuerSmallBusiness) continue;
    if (!relationMatches(r.recipient, issuer, recipient)) continue;
    if (r.recipientIn && !r.recipientIn.includes(recipient)) continue;
    if (r.aboveSmallAmount && small) continue;
    if (r.aboveGross && !(input.currency === r.aboveGross.currency && input.grossCents > r.aboveGross.cents)) continue;
    if (r.recipientBusiness !== undefined && input.recipientIsBusiness !== r.recipientBusiness) continue;
    return { fields: [...r.fields], ruleId: r.id, severity: "hint" };
  }
  return { fields: [], ruleId: null, severity: "hint" };
}

/** Für das Profil: Pflicht, die im Inlandsfall über der Kleinbetragsgrenze gilt. */
export function profileTaxIdRequirement(
  issuer: { country: string | null; smallBusiness: boolean },
  config: TaxConfig = DEFAULT_TAX_CONFIG,
): string[] {
  return taxIdRequirement(
    {
      issuerCountry: issuer.country,
      issuerSmallBusiness: issuer.smallBusiness,
      recipientCountry: issuer.country,
      grossCents: Number.MAX_SAFE_INTEGER,
      currency: "EUR",
    },
    config,
  ).fields;
}

// ---------------------------------------------------------------------------
// determineTaxTreatment
// ---------------------------------------------------------------------------

export interface TaxInput {
  issuerCountry: string | null;
  issuerSmallBusiness: boolean;
  issuerVatId: string | null;
  issuerTaxNumber?: string | null;
  recipientCountry: string | null;
  recipientIsBusiness: boolean;
  recipientVatId: string | null;
  currency: Currency;
  grossCents: Cents;
  rateCategory?: RateCategory;
  /**
   * Leistungsdatum bzw. Beginn des Leistungszeitraums (Befund 2). Nur wenn angegeben, prüft die Funktion die Pflichtangabe
   * (null = fehlt); ohne das Feld bleibt die Prüfung wie bisher dem Aufrufer überlassen.
   */
  serviceDate?: string | null;
}

/** Pflichtfelder der Steuerprüfung in Klartext (statt interner Schlüssel wie „issuerTaxNumberOrVatId“, Befund 3) */
export function taxFieldLabel(field: string, ctx: { issuer?: "agency" | "creator"; recipientCountry?: string | null; issuerCountry?: string | null } = {}): string {
  const who = ctx.issuer === "creator" ? "des Creators" : "der Agentur";
  switch (field) {
    case F.issuerVatId: return `${taxIdLabel(ctx.issuerCountry ?? null, "de")} ${who}`;
    case F.issuerTaxNumberOrVatId: return `Steuernummer oder ${taxIdLabel(ctx.issuerCountry ?? null, "de")} ${who}`;
    case F.issuerChUid: return `UID-Nummer ${who}`;
    case F.recipientVatId: return `${taxIdLabel(ctx.recipientCountry ?? null, "de")} des Empfängers`;
    case F.serviceDate: return "Leistungsdatum bzw. Leistungszeitraum";
    case F.chQrIban: return "Konto in der Schweiz oder in Liechtenstein (IBAN oder QR-IBAN) für den QR-Zahlteil";
    case F.chQrCreditor: return "Name und Anschrift für den QR-Zahlteil";
    case F.chQrAmount: return "Betrag und Währung für den QR-Zahlteil";
    case F.chQrReference: return "Referenz für den QR-Zahlteil";
    default: return field;
  }
}

export interface TaxTreatment {
  mode: TaxMode;
  ratePercent: number;
  noticeKey: string;
  requiredFields: string[];
  missingFields: string[];
  manualReview: boolean;
  ruleId: string | null;
  issuerZone: TaxZone;
  recipientZone: TaxZone;
  smallAmountInvoice: boolean;
  findings: Finding[];
}

function issuerMatches(m: IssuerMatch, issuer: CountryCode): boolean {
  if (m.except?.includes(issuer)) return false;
  if (m.exceptGroup === "EU" && isEuCountry(issuer)) return false;
  if (m.countries) return m.countries.includes(issuer);
  if (m.group === "ANY") return true;
  if (m.group === "EU") return isEuCountry(issuer);
  return false;
}

function has(v: string | null | undefined): boolean {
  return !!v && v.trim() !== "";
}

/**
 * K-287 (Schweiz-Prüfung 14.10.2026): Auftritte, Events, Moderationen und ähnliche Leistungen vor Ort haben in der Schweiz und in
 * Liechtenstein ihren Leistungsort am Ort der Tätigkeit (Art. 8 Abs. 2 lit. c MWSTG), nicht am Sitz des Empfängers. Die
 * Steuerregeln kennen den Ort der Tätigkeit nicht; deshalb nur ein Prüfhinweis, wenn eine Position danach klingt.
 */
const ACTIVITY_WORDS = /(^|[^a-zäöü])(events?|auftritt\w*|moderation\w*|moderier\w*|live-?\w*|messe\w*|shows?|workshops?|vortr[aä]g\w*|keynotes?|meet ?(and|&) ?greet|vor ort|on ?site|appearance|hosting)([^a-zäöü]|$)/i;

export function activityPlaceHint(input: { issuerCountry: string | null; recipientCountry: string | null; descriptions: readonly string[] }): boolean {
  const ch = (c: string | null) => ["CH", "LI"].includes(normalizeCountry(c) ?? "");
  if (!ch(input.issuerCountry) && !ch(input.recipientCountry)) return false;
  return input.descriptions.some((d) => ACTIVITY_WORDS.test(d));
}

/**
 * Steuerprüfung 23.10.2026, L8 und L13: Prüfhinweise zum Leistungsort, die die Steuerregeln nicht selbst entscheiden (kein neuer
 * Steuerfall, nur Hinweise im Generator).
 * - CH_ACTIVITY: K-287, Auftritte vor Ort mit Bezug zur Schweiz oder zu Liechtenstein (Art. 8 Abs. 2 lit. c MWSTG).
 * - B2C_ACTIVITY (L8): Aussteller in DE/AT, Auftritt oder Event an eine Privatperson: Ort der Tätigkeit (§ 3a Abs. 3 Nr. 3
 *   Buchst. a UStG, § 3a Abs. 11 lit. a UStG 1994).
 * - B2C_THIRD_COUNTRY (L8): Aussteller in DE/AT, Privatperson mit Wohnsitz außerhalb der EU: Werbeleistungen dort steuerbar
 *   (§ 3a Abs. 4 Satz 2 Nr. 2 UStG, § 3a Abs. 13 und 14 Z 2 UStG 1994), andere Leistungen am Sitz des Ausstellers.
 * - CH_B2C_REGISTRATION (L13): Aussteller außerhalb von CH/LI, Privatperson in CH/LI: Leistungsort dort (Art. 8 Abs. 1 MWSTG),
 *   ab 100.000 CHF weltweitem Umsatz Steuerpflicht und Registrierung dort (Art. 10 Abs. 1 und 2 lit. a MWSTG).
 */
export type PlaceOfSupplyHint = "CH_ACTIVITY" | "B2C_ACTIVITY" | "B2C_THIRD_COUNTRY" | "CH_B2C_REGISTRATION";

export function placeOfSupplyHints(input: {
  issuerCountry: string | null; recipientCountry: string | null; recipientIsBusiness: boolean; descriptions: readonly string[];
}): PlaceOfSupplyHint[] {
  const issuer = normalizeCountry(input.issuerCountry);
  const recipient = normalizeCountry(input.recipientCountry);
  const chLi = (c: CountryCode | null) => c === "CH" || c === "LI";
  const dach = issuer === "DE" || issuer === "AT";
  const activity = input.descriptions.some((d) => ACTIVITY_WORDS.test(d));
  const out: PlaceOfSupplyHint[] = [];
  if (activityPlaceHint(input)) out.push("CH_ACTIVITY");
  if (input.recipientIsBusiness) return out;
  if (dach && activity) out.push("B2C_ACTIVITY");
  if (dach && recipient && recipient !== issuer && !isEuCountry(recipient)) out.push("B2C_THIRD_COUNTRY");
  if (issuer && !chLi(issuer) && chLi(recipient)) out.push("CH_B2C_REGISTRATION");
  return out;
}

/** Text eines Prüfhinweises zum Leistungsort (Generator), mit der Fundstelle des Ausstellerlands */
export function placeOfSupplyHintText(hint: PlaceOfSupplyHint, lang: Language, issuerCountry: string | null): string {
  const at = normalizeCountry(issuerCountry) === "AT";
  const en = lang === "en";
  switch (hint) {
    case "CH_ACTIVITY":
      return en ? "Performances, events and hosting on site have their place of supply where the activity takes place (Art. 8(2)(c) Swiss VAT Act), not at the recipient's seat: please check the tax treatment."
        : "Auftritte, Events und Moderationen vor Ort haben ihren Leistungsort am Ort der Tätigkeit (Art. 8 Abs. 2 lit. c MWSTG), nicht am Sitz des Empfängers: Steuerfall bitte prüfen.";
    case "B2C_ACTIVITY":
      return en ? `Performances, events and hosting for private individuals have their place of supply where the activity takes place (${at ? "§ 3a(11)(a) Austrian VAT Act" : "§ 3a(3) no. 3(a) German VAT Act"}), not at the issuer's seat. If it takes place in another country, VAT is due there: please check the tax treatment.`
        : `Auftritte, Events und Moderationen an Privatpersonen haben ihren Leistungsort dort, wo die Tätigkeit ausgeübt wird (${at ? "§ 3a Abs. 11 lit. a UStG 1994" : "§ 3a Abs. 3 Nr. 3 Buchst. a UStG"}), nicht am Sitz des Ausstellers. Findet sie in einem anderen Land statt, fällt die Steuer dort an: Steuerfall bitte prüfen.`;
    case "B2C_THIRD_COUNTRY":
      return en ? `Advertising services to private individuals resident outside the EU are taxable where they live and not taxable in ${at ? "Austria (§ 3a(13) and (14) no. 2 Austrian VAT Act)" : "Germany (§ 3a(4) sentence 2 no. 2 German VAT Act)"}. Other services to private individuals remain taxable at the issuer's seat. Please choose the tax treatment that applies.`
        : `Werbeleistungen an Privatpersonen mit Wohnsitz außerhalb der EU sind dort steuerbar und in ${at ? "Österreich nicht steuerbar (§ 3a Abs. 13 und 14 Z 2 UStG 1994)" : "Deutschland nicht steuerbar (§ 3a Abs. 4 Satz 2 Nr. 2 UStG)"}. Andere Leistungen an Privatpersonen bleiben am Sitz des Ausstellers steuerbar. Bitte den zutreffenden Steuerfall wählen.`;
    case "CH_B2C_REGISTRATION":
      return en ? "Services to private individuals in Switzerland or Liechtenstein have their place of supply there (Art. 8(1) Swiss VAT Act). From CHF 100,000 worldwide annual turnover the issuer becomes liable for VAT there and must register (Art. 10(1) and (2)(a) Swiss VAT Act); the private individual's reverse-charge tax does not replace this."
        : "Leistungen an Privatpersonen in der Schweiz oder in Liechtenstein haben ihren Leistungsort dort (Art. 8 Abs. 1 MWSTG). Ab 100.000 CHF Jahresumsatz weltweit wird der Aussteller dort mehrwertsteuerpflichtig und muss sich registrieren (Art. 10 Abs. 1 und 2 lit. a MWSTG); die Bezugsteuer der Privatperson ersetzt das nicht.";
  }
}

/**
 * K-269 (Schweiz-Prüfung 14.10.2026): Im MWST-Register eingetragen ist, wer eine Nummer mit Zusatz „MWST“ (bzw. „TVA“, „IVA“)
 * hat, z. B. „CHE-123.456.789 MWST“ (Art. 26 Abs. 2 lit. a MWSTG). Die blosse UID ohne Zusatz ist keine MWST-Nummer. Liechtenstein:
 * jede hinterlegte MWST-Nummer. Andere Länder: immer true (dort entscheidet das Steuerprofil).
 */
export function isChVatRegistered(country: string | null | undefined, vatId: string | null | undefined): boolean {
  const c = normalizeCountry(country ?? null);
  if (c === "LI") return has(vatId);
  if (c !== "CH") return true;
  const v = (vatId ?? "").replace(/[\s.\-]/g, "").toUpperCase();
  return /^CHE\d{9}(MWST|TVA|IVA|VAT)$/.test(v);
}

export function determineTaxTreatment(input: TaxInput, config: TaxConfig = DEFAULT_TAX_CONFIG): TaxTreatment {
  const issuer = normalizeCountry(input.issuerCountry);
  const recipient = normalizeCountry(input.recipientCountry);
  const issuerZone = zoneOfCountry(issuer);
  const recipientZone = zoneOfCountry(recipient);
  const findings: Finding[] = [];

  const manual = (reason: string): TaxTreatment => ({
    mode: "MANUAL",
    ratePercent: 0,
    noticeKey: "MANUAL_REVIEW",
    requiredFields: [],
    missingFields: [],
    manualReview: true,
    ruleId: null,
    issuerZone,
    recipientZone,
    smallAmountInvoice: false,
    findings: [...findings, { code: reason, severity: "warning" }],
  });

  if (!issuer) return manual("ISSUER_COUNTRY_UNKNOWN");
  if (!recipient) return manual("RECIPIENT_COUNTRY_UNKNOWN");

  // K-269: Aussteller in CH/LI ohne MWST-Nummer gelten als nicht mehrwertsteuerpflichtig (keine 8,1 % ohne Registrierung)
  const chUnregistered = (issuer === "CH" || issuer === "LI") && !input.issuerSmallBusiness && !isChVatRegistered(issuer, input.issuerVatId);
  if (chUnregistered) findings.push({ code: "CH_VAT_NUMBER_MISSING", severity: "hint" });
  const issuerSmall = input.issuerSmallBusiness || chUnregistered;

  const rule = config.rules.find(
    (r) =>
      issuerMatches(r.issuer, issuer) &&
      (r.issuerSmallBusiness === undefined || r.issuerSmallBusiness === issuerSmall) &&
      relationMatches(r.recipient, issuer, recipient) &&
      (!r.recipientIn || r.recipientIn.includes(recipient)) &&
      (r.recipientIsBusiness === undefined || r.recipientIsBusiness === input.recipientIsBusiness),
  );
  if (!rule) return manual("NO_TAX_RULE");

  let rate = 0;
  if (rule.rate === "domestic") {
    const table = vatRatesOn(issuer, input.serviceDate);
    const cat = input.rateCategory ?? "standard";
    const r = table?.[cat] ?? table?.standard;
    if (r === undefined) return manual("NO_RATE_DATA");
    if (table && table[cat] === undefined) findings.push({ code: "RATE_CATEGORY_UNKNOWN", severity: "hint", params: { category: cat } });
    rate = clampRatePercent(r);
  }

  const smallAmount =
    isSmallAmountInvoice({ issuerCountry: issuer, currency: input.currency, grossCents: input.grossCents }, config) &&
    (rule.mode === "DOMESTIC" || rule.mode === "SMALL_BUSINESS");

  const idReq = taxIdRequirement(
    {
      issuerCountry: issuer,
      issuerSmallBusiness: issuerSmall,
      recipientCountry: recipient,
      grossCents: input.grossCents,
      currency: input.currency,
      recipientIsBusiness: input.recipientIsBusiness,
    },
    config,
  );

  const required = new Set<string>([...rule.requiredFields, ...idReq.fields]);
  if (!smallAmount) required.add(F.serviceDate);
  // QR-Rechnung: Schweiz und Liechtenstein (ein Zahlungsraum, Swiss Payment Standards)
  if ((issuer === "CH" || issuer === "LI") && config.chQrBillCurrencies.includes(input.currency)) {
    for (const f of [F.chQrIban, F.chQrCreditor, F.chQrAmount, F.chQrReference]) required.add(f);
  }

  const missing: string[] = [];
  for (const f of required) {
    if (f === F.issuerVatId && !has(input.issuerVatId)) missing.push(f);
    if (f === F.recipientVatId && !has(input.recipientVatId)) missing.push(f);
    if (f === F.issuerChUid && !has(input.issuerVatId)) missing.push(f);
    if (f === F.issuerTaxNumberOrVatId && !has(input.issuerVatId) && !has(input.issuerTaxNumber)) missing.push(f);
    if (f === F.serviceDate && "serviceDate" in input && !has(input.serviceDate ?? null)) missing.push(f);
  }
  for (const f of missing) findings.push({ code: "MISSING_TAX_FIELD", severity: "hint", params: { field: f } });

  // 0444 (L7): Aussteller außerhalb der EU ohne USt-IdNr. eines Mitgliedstaats (die heimische Nummer ist keine)
  if (rule.id === "FOREIGN_TO_EU_RC" && !isEuVatIdFormat(input.issuerVatId)) findings.push({ code: "ISSUER_NO_EU_VAT_ID", severity: "question" });

  const issuerVatHint = checkVatIdFormat(input.issuerVatId, issuer);
  if (issuerVatHint) findings.push(issuerVatHint);
  const recipientVatHint = checkVatIdFormat(input.recipientVatId, recipient);
  if (recipientVatHint) findings.push(recipientVatHint);

  return {
    mode: rule.mode,
    ratePercent: rate,
    noticeKey: rule.noticeKey,
    requiredFields: [...required],
    missingFields: missing,
    manualReview: !!rule.manualReview,
    ruleId: rule.id,
    issuerZone,
    recipientZone,
    smallAmountInvoice: smallAmount,
    findings,
  };
}

// ---------------------------------------------------------------------------
// Profil des Ausstellers (ST-22, WS-08)
// ---------------------------------------------------------------------------

export interface IssuerProfile {
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  iban?: string | null;
  accountHolder?: string | null;
  bankName?: string | null;
  bic?: string | null;
  smallBusiness: boolean;
  vatId?: string | null;
  taxNumber?: string | null;
}

export type ProfileField = Exclude<keyof IssuerProfile, "smallBusiness" | "vatId" | "taxNumber">;

export const DEFAULT_PROFILE_REQUIRED_FIELDS: readonly ProfileField[] = [
  "street",
  "postalCode",
  "city",
  "country",
  "iban",
  "accountHolder",
  "bankName",
  "bic",
];

/**
 * Vollständigkeit des Aussteller-Profils. Das Land ist immer Pflicht (WS-08: das
 * Ausstellerland kommt aus dem Profil). Die Steuernummer-Pflicht kommt aus WS-07.
 */
export function checkIssuerProfile(
  profile: IssuerProfile,
  requiredFields: readonly ProfileField[] = DEFAULT_PROFILE_REQUIRED_FIELDS,
  config: TaxConfig = DEFAULT_TAX_CONFIG,
): { complete: boolean; missing: string[] } {
  const fields = new Set<ProfileField>([...requiredFields, "country"]);
  const missing: string[] = [];
  for (const f of fields) if (!has(profile[f] ?? null)) missing.push(f);
  if (has(profile.country ?? null) && !normalizeCountry(profile.country ?? null)) missing.push("country:unknown");
  for (const f of profileTaxIdRequirement({ country: profile.country ?? null, smallBusiness: profile.smallBusiness }, config)) {
    if (f === F.issuerTaxNumberOrVatId && !has(profile.vatId) && !has(profile.taxNumber)) missing.push(f);
    if ((f === F.issuerVatId || f === F.issuerChUid) && !has(profile.vatId)) missing.push(f);
  }
  return { complete: missing.length === 0, missing };
}
